import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { STORAGE_KEYS } from "@currencycore/shared";
import { organization } from "../lib/auth-client.ts";
import { useSession } from "../lib/session.tsx";

/**
 * Handles the "Accept invitation" link from the invite email
 * (`/accept-invitation/:invitationId`). Without this route the link fell through
 * to the catch-all redirect and the invite was never accepted.
 *
 * Flow: require a session (bounce to login + return), accept the invitation via
 * the native Better-Auth endpoint, then HARD-navigate to the new org so the org
 * list + session refetch cleanly (a soft navigate races the dashboard's
 * org-guard, which would bounce on a stale list).
 */
export function AcceptInvitationPage() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (isPending || ran.current) return;
    if (!session) {
      // Must be signed in as the invited email to accept. Sign in, then return.
      navigate(`/login?next=${encodeURIComponent(`/accept-invitation/${invitationId}`)}`, { replace: true });
      return;
    }
    if (!invitationId) {
      setError("This invitation link is invalid.");
      return;
    }
    ran.current = true;
    void (async () => {
      try {
        const res = await (organization as unknown as {
          acceptInvitation: (b: { invitationId: string }) => Promise<{
            data?: { invitation?: { organizationId?: string }; member?: { organizationId?: string } } | null;
            error?: { message?: string } | null;
          }>;
        }).acceptInvitation({ invitationId });
        if (res?.error) {
          setError(
            res.error.message ??
              "This invitation could not be accepted. It may have expired, been revoked, or be for a different email than you're signed in with.",
          );
          return;
        }
        const orgId = res?.data?.invitation?.organizationId ?? res?.data?.member?.organizationId ?? null;
        if (orgId) {
          try { localStorage.setItem(STORAGE_KEYS.lastOrgId, orgId); } catch { /* ignore */ }
        }
        window.location.assign(orgId ? `/dashboard/${orgId}` : "/dashboard");
      } catch {
        setError("Something went wrong accepting this invitation. Please try again.");
      }
    })();
  }, [session, isPending, invitationId, navigate]);

  return (
    <div className="auth-page">
      <div className="loading-stack">
        {error ? (
          <>
            <h1 style={{ fontSize: 20, marginBottom: 6 }}>Couldn't accept the invitation</h1>
            <p style={{ color: "var(--text-subtle)", maxWidth: 380, textAlign: "center" }}>{error}</p>
            <a className="btn btn-primary" href="/dashboard" style={{ marginTop: 14 }}>Go to dashboard</a>
          </>
        ) : (
          <>
            <span className="spinner spinner-lg" />
            <p>Accepting your invitation…</p>
          </>
        )}
      </div>
    </div>
  );
}
